<template>
  <AppDialog
    class="team-wizard-modal"
    title="New team"
    :show="show"
    append-to-body
    data-testid="team-wizard-dialog"
    @close="closeWizard"
  >
    <AppWizard
      :value="step"
      :steps="wizardSteps"
    >
      <AppWizardStep
        v-show="step === 0"
        :show-back-button="false"
        save-and-close
        :save-button-text="saveAndExitText"
        :disabled="disabled"
        data-testid="team-wizard-step-create"
        @previous="previousStep"
        @next="nextStep"
        @close="closeWizard"
        @save-and-exit="saveAndExit"
      >
        <div class="row">
          <div class="col-sm-12">
            <div
              class="form-group"
              :class="{ 'has-error': hasErrorMessage }"
              data-testid="team-name-input"
            >
              <label
                class="control-label required"
                for="team-name"
              >
                Team name
                <abbr title="required">*</abbr>
              </label>
              <AppInput
                id="team-name"
                v-model="teamName"
                type="text"
                class="team-name"
                placeholder="Enter a team name"
              />
              <span
                v-show="hasErrorMessage"
                class="help-block"
              >
                {{ errorMessage }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="hasTeamsSyncFlag && showLinkOption"
          class="row"
        >
          <div class="col-sm-12">
            <div class="form-group">
              <div class="checkbox">
                <AppCheckbox
                  :value="linkTeam"
                  @input="onSelectLinkTeam"
                >
                  Link to
                  <img
                    class="label-logo"
                    :src="tasksLogo"
                  />
                  organization
                </AppCheckbox>
              </div>
            </div>
          </div>
        </div>
      </AppWizardStep>

      <AppWizardStep
        v-show="step === 1"
        show-back-button
        :is-last-step="!showProjects"
        :save-button-text="saveButtonText"
        :disabled="disabled"
        data-testid="team-wizard-step-members"
        @previous="previousStep"
        @close="closeWizard"
        @next="nextStep"
        @input="saveAndExit"
      >
        <AppLazySelect
          ref="user-selector"
          v-model="member"
          eager
          multiple
          append-to-body
          label="Members"
          title="Select team members"
          resource="member"
          class="mb-20"
          :fetcher="fetcher"
          :organization-id="organizationId"
          data-testid="team-members-select"
          @input="submitSelections($event, 'user')"
        >
          <template #item-leading="{ item }">
            <AppAvatar
              v-if="item.avatar"
              :avatar="item.avatar"
              size="speck"
            />
          </template>
        </AppLazySelect>

        <AppLazySelect
          ref="lead-selector"
          v-model="lead"
          eager
          multiple
          append-to-body
          label="Team leads"
          title="Select team leads"
          resource="member"
          :fetcher="fetcher"
          :organization-id="organizationId"
          data-testid="team-leads-select"
          @input="submitSelections($event, 'lead')"
        >
          <template #after-label>
            <i
              v-tooltip
              class="hi hi-info-filled hi-12 a-tooltip"
              :title="teamLeadTooltip"
            />
          </template>
          <template #item-leading="{ item }">
            <AppAvatar
              v-if="item.avatar"
              :avatar="item.avatar"
              size="speck"
            />
          </template>
        </AppLazySelect>
        <div class="app-members-selector-footnote">You can customize the team lead permissions later.</div>

        <div
          v-if="!showProjects"
          class="form-group mt-20"
        >
          <div class="checkbox">
            <AppCheckbox
              v-model="scheduleWeeklyReport"
              :disabled="disabled"
            >
              <i class="hi hi-time hi-16 mr-5" />
              Schedule weekly report
              <i
                v-tooltip
                class="hi hi-info-filled hi-12 a-tooltip ml-5"
                :title="weeklyReportTooltip"
              />
            </AppCheckbox>
          </div>
        </div>
      </AppWizardStep>

      <AppWizardStep
        v-if="showProjects"
        v-show="step === 2"
        id="team-projects"
        is-last-step
        show-back-button
        :save-button-text="saveButtonText"
        :disabled="disabled"
        data-testid="team-wizard-step-projects"
        @previous="previousStep"
        @close="closeWizard"
        @input="saveAndExit"
      >
        <AppLazySelect
          ref="project-selector"
          v-model="teamProjectIds"
          multiple
          append-to-body
          label="Projects"
          title="Select projects"
          resource="project"
          :fetcher="fetchProjects"
          :organization-id="organizationId"
          data-testid="team-projects-select"
          @input="onSelectProjects"
        />
        <div class="form-group mt-20">
          <div class="checkbox">
            <AppOnOffToggle
              v-model="scheduleWeeklyReport"
              :disabled="disabled"
            >
              <span class="text-right">
                Schedule weekly report
                <i
                  v-tooltip
                  class="hi hi-info-filled hi-12 a-tooltip ml-5"
                  :title="weeklyReportTooltip"
                />
              </span>
            </AppOnOffToggle>
          </div>
        </div>
      </AppWizardStep>
    </AppWizard>

    <TeamScheduledReportDialog
      :show="showReportDialog"
      :team="createdTeam"
      :organization="organization"
      @success="onReportSuccess"
      @close="onCloseReportDialog"
    />
  </AppDialog>
</template>

<script>
import { ProjectsResource, TeamsResource, UserOrganizationsResource } from '~/resources';
import { IMAGE_HS_TASKS_LOGO } from '~/rails_assets';
import { trim } from 'lodash-es';
import { mapState } from 'vuex';
import TeamScheduledReportDialog from '~/components/teams/TeamScheduledReportDialog.vue';

const MAX_TEAMS_FOR_CHECKED_SCHEDULE = 4;

export default {
  name: 'TeamWizard',
  components: {
    TeamScheduledReportDialog,
  },
  data: () => ({
    step: 0,
    fullTeamWizardSteps: Object.freeze([{ title: 'Create team' }, { title: 'Add members' }, { title: 'Add projects' }]),
    shortTeamWizardSteps: Object.freeze([{ title: 'Create team' }, { title: 'Add members' }]),
    errorMessage: false,
    select2Options: {
      allowClear: true,
      multiple: true,
      width: '100%',
    },
    team: { name: '' },
    teamMembers: { userIds: [] },
    teamLeads: { userIds: [] },
    teamProjects: { projectIds: [] },
    linkTeam: false,
    saving: false,
    options: null,
    show: true,
    users: [],
    leads: [],
    scheduleWeeklyReport: true,
    showReportDialog: false,
    createdTeam: null,
  }),
  computed: {
    ...mapState(['organization', 'team_names', 'projects']),
    teamName: {
      get() {
        return this.team.name;
      },
      set(value) {
        let errorMessage = '';

        if (value.length === 0) {
          errorMessage = "can't be blank";
        }
        this.errorMessage = errorMessage;
        if (this.team.name !== value) {
          this.team.name = value;
        }
      },
    },
    teamProjectIds: {
      get() {
        return this.teamProjects.projectIds;
      },
      set(projects) {
        this.onSelectProjects(projects);
      },
    },
    projectOptions() {
      const projectFilter = (p) => {
        const showAll = this.linkTeam || this.isLinkedViaAddon;

        if (this.hasTeamsSyncFlag) {
          return showAll ? true : !Number.isInteger(p.sync_version);
        }

        return true;
      };

      return this.projects.filter(projectFilter).map((project) => ({ id: project.id, text: project.name }));
    },
    allProjectIds() {
      return this.projectOptions.map((project) => {
        return project.id.toString();
      });
    },
    projectsSelect2() {
      return { ...this.select2Options, ...{ placeholder: 'Select projects' } };
    },
    hasErrorMessage() {
      return !!this.errorMessage;
    },
    saveAndExitText() {
      return this.saving ? 'Saving...' : 'Save and close';
    },
    saveButtonText() {
      return this.saving ? 'Saving...' : 'Save';
    },
    disabled() {
      return this.saving;
    },
    showProjects() {
      // TODO: Fix this after this PRs has been merged in order
      //       to follow the same approach with feature checking.
      //
      // PR: https://github.com/NetsoftHoldings/hubstaff-server/pull/5899
      return this.organization.features.projects;
    },
    wizardSteps() {
      return this.showProjects ? this.fullTeamWizardSteps : this.shortTeamWizardSteps;
    },
    teamLeadTooltip() {
      const projectData = ' and all the projects assigned to it';

      return `Team leads manage the team ${
        this.showProjects ? projectData : ''
      }. Their extra permissions include approving timesheets, time off requests, and creating schedules for the team.`;
    },
    teamId() {
      return this.team?.id || null;
    },
    organizationId() {
      return this.organization.id;
    },
    includeLinkedProjects() {
      return !this.hasTeamsSyncFlag || this.linkTeam || (this.hasTeamsSyncFlag && this.isLinkedViaAddon);
    },
    tasksLogo() {
      return IMAGE_HS_TASKS_LOGO;
    },
    hasTeamsSyncFlag() {
      return this.organization.flags.includes('teams_sync');
    },
    isLinkedViaAddon() {
      return !!this.organization.linked_via_addon;
    },
    showLinkOption() {
      // Organization is linked and has NO tasks addon
      return Number.isInteger(this.organization.sync_version) && !this.organization.linked_via_addon;
    },
    resource() {
      return TeamsResource.members;
    },
    ...Object.fromEntries(
      ['member', 'lead'].map((role) => [
        role,
        {
          get() {
            return this[`team${role[0].toUpperCase() + role.slice(1)}s`].userIds;
          },
          set(items) {
            const otherRole = role === 'member' ? 'lead' : 'member';
            const otherRoleKey = `team${otherRole[0].toUpperCase() + otherRole.slice(1)}s`;

            // Remove items from the other role
            this[otherRoleKey].userIds = this[otherRoleKey].userIds.filter((id) => !items.includes(id));

            // Update the current role
            this[`team${role[0].toUpperCase() + role.slice(1)}s`].userIds = items;
          },
        },
      ]),
    ),
    weeklyReportTooltip() {
      return 'Automatically schedule a weekly time and activity report for this team to be sent to you and the team leads.';
    },
  },
  watch: {
    step() {
      ['user-selector', 'lead-selector', 'project-selector'].forEach((selector) => {
        this.$refs[selector]?.closeOrApply?.();
      });
    },
  },
  created() {
    this.scheduleWeeklyReport = this.team_names.length < MAX_TEAMS_FOR_CHECKED_SCHEDULE;
  },
  methods: {
    resetWizard() {
      this.step = 0;
      this.team.name = '';
      this.teamMembers = { userIds: [] };
      this.teamLeads = { userIds: [] };
      this.teamProjects = { projectIds: [] };
      this.saving = false;
      this.errorMessage = false;
      this.scheduleWeeklyReport = this.team_names.length < MAX_TEAMS_FOR_CHECKED_SCHEDULE;
      this.showReportDialog = false;
      this.createdTeam = null;
    },
    closeWizard() {
      this.resetWizard();
      this.show = false;
      this.$emit('close');
    },
    checkUniqueName() {
      let uniqueName = false;
      const teamName = trim(this.team.name).toLowerCase();
      const existingTeam = this.team_names.find(function (team) {
        return team.toLowerCase() === teamName;
      });

      if (!existingTeam) {
        const errorMessage = '';

        this.errorMessage = errorMessage;
        uniqueName = true;
      }

      return uniqueName;
    },
    previousStep() {
      this.step--;
    },
    nextStep() {
      if (this.team.name.length > 0 && this.errorMessage.length === 0) {
        this.step++;
      } else {
        let errorMessage = '';

        if (this.team.name.length === 0) {
          errorMessage = "can't be blank";
        }
        this.errorMessage = errorMessage;
      }
    },
    async saveAndExit() {
      this.saving = true;

      const uniqueName = this.checkUniqueName();
      const validLength = trim(this.team.name).length > 0;

      if (validLength && uniqueName && this.errorMessage.length === 0) {
        // If the team is created from the first step of the wizard, we don't want to schedule a weekly report
        if (this.step === 0) {
          this.scheduleWeeklyReport = false;
        }

        const data = {
          team: {
            organization_id: this.organization.id,
            name: this.team.name,
          },
          teams: {
            members: this.teamMembers.userIds,
            leads: this.teamLeads.userIds,
            projects: this.teamProjects.projectIds,
          },
          link_team: this.linkTeam,
          schedule_weekly_report: this.scheduleWeeklyReport,
        };

        try {
          this.createdTeam = await TeamsResource.create({ id: this.organization.id }, { ...data, options: this.options });

          if (this.scheduleWeeklyReport) {
            this.showReportDialog = true;
          } else {
            window.location = window.location.pathname;
          }
        } catch (err) {
          if (err?.responseJSON?.error === 'view_only_team_leads') {
            this.step = 1;
          }

          const message = err?.responseJSON?.alert || 'Unable to create the team';

          this.$appMessage(message);

          throw new Error(message);
        } finally {
          this.saving = false;
        }
      } else {
        this.saving = false;
        let errorMessage;

        if (!validLength) {
          errorMessage = "can't be blank";
        } else if (!uniqueName) {
          errorMessage = 'A team with the same name already exists';
        }
        this.step = 0;
        this.errorMessage = errorMessage;
      }
    },
    onSelectProjects(projects) {
      this.teamProjects.projectIds = projects;
    },
    onSelectLinkTeam(bool) {
      this.linkTeam = bool;
    },
    fetcher(payload) {
      return ProjectsResource.members({ organization_id: this.organizationId }, payload, {
        abortReload: true,
      });
    },
    fetchProjects(payload) {
      return UserOrganizationsResource.projects(
        { id: this.organizationId }, { ...payload, prefix_ids: false, include_linked: this.includeLinkedProjects },
        { abortReload: true, }
      );
    },
    async submitSelections(items = [], role = 'user') {
      ['user', 'lead']
        .filter((r) => r !== role)
        .map((role) => this.$refs[`${role}-selector`].excludeItems(items))
        .filter(Boolean);
    },
    onReportSuccess(message) {
      this.$appMessage(message);
    },
    onCloseReportDialog() {
      this.showReportDialog = false;
      this.closeWizard();
      window.location = window.location.pathname;
    },
  },
};
</script>
